import { type TFunction } from "i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import {
  type LeadQuickView,
  type ProjectQuickView
} from "../redux/global-map.types"

interface FieldRow {
  label: string
  value: string
}

interface LocationBadge {
  text: string
  approximate: boolean
}

// Redacted/absent fields never reach the client as `null` — the serializer
// simply omits the key (spec §8) — so a row is dropped whenever its source
// value is `undefined` or `null`, rather than rendered blank/placeholder.
//
// Status and Address are deliberately NOT field rows here — per the client's
// own before/after reference (`Before.docx`, 2026-09-19), Status moved up
// into the header's own pill and Address moved into the header's identity
// block (under the title), so keeping them here too would just duplicate
// what the header already shows. Order otherwise matches that same
// reference: Customer, Project Manager, Salesperson, Subcontractor, Start
// Date, End Date, Project Type, Estimated Value. Progress and SLA Status are
// deliberately NOT plain field rows — Progress renders as a real bar
// (`EntityQuickView.tsx`'s own dedicated markup, matching the real Project
// Progress bar's own presentation) and SLA Status has no backing data on
// this payload at all. Location precision is not a field row either — the
// `locationBadge` helper below already surfaces it as the pin's
// approximate/precise badge.
export const toFieldRows = (qv: ProjectQuickView, t: TFunction): FieldRow[] => {
  const candidates: Array<{ label: string; value: string | null | undefined }> =
    [
      {
        label: t("globalMap.quickView.fields.customer"),
        value: qv.people.customer
      },
      {
        label: t("globalMap.quickView.fields.projectManager"),
        value: qv.people.project_manager
      },
      {
        label: t("globalMap.quickView.fields.salesperson"),
        value: qv.people.salesperson
      },
      {
        label: t("globalMap.quickView.fields.subcontractor"),
        value: qv.people.subcontractors
      },
      {
        label: t("globalMap.quickView.fields.startDate"),
        value: getFormatDate(qv.lifecycle.start_date)
      },
      {
        label: t("globalMap.quickView.fields.endDate"),
        value: getFormatDate(qv.lifecycle.end_date)
      },
      {
        label: t("globalMap.quickView.fields.projectType"),
        value: qv.project_type
      },
      {
        label: t("globalMap.quickView.fields.estimatedValue"),
        value:
          qv.estimated_value !== null ? budgetFormat(qv.estimated_value) : null
      }
    ]

  return candidates.filter(
    (row): row is FieldRow => row.value !== undefined && row.value !== null
  )
}

// `LeadQuickView`'s own field set: no customer/project-manager/salesperson,
// project type, or start/end dates — a Lead has an owner and a follow-up
// date instead. Same drop-when-absent-or-null rule as `toFieldRows`, and the
// same Status/Address-are-header-content exclusion — see the comment above.
export const toLeadFieldRows = (
  qv: LeadQuickView,
  t: TFunction
): FieldRow[] => {
  const candidates: Array<{ label: string; value: string | null | undefined }> =
    [
      { label: t("globalMap.quickView.fields.owner"), value: qv.people.owner },
      {
        label: t("globalMap.quickView.fields.followUpDate"),
        value: getFormatDate(qv.lifecycle.follow_up_date)
      }
    ]

  return candidates.filter(
    (row): row is FieldRow => row.value !== undefined && row.value !== null
  )
}

// `location`/`precision` are identical shapes on both entities
// (`LeadQuickView["location"]` is literally `ProjectQuickView["location"]`),
// so this takes either union member rather than `ProjectQuickView` alone.
export const locationBadge = (
  qv: ProjectQuickView | LeadQuickView,
  t: TFunction
): LocationBadge => {
  const approximate = qv.location.precision === "approximate"

  return {
    text: t(
      approximate
        ? "globalMap.quickView.approximateLocation"
        : "globalMap.quickView.preciseLocation"
    ),
    approximate
  }
}

export type { FieldRow, LocationBadge }
